/** @format */

import React from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { IRecoveryFunnel } from "../../../utility/types/collectTypes";
import { colors } from "../../../utility/types/constant";
import {
    CollectSectionCard,
    CollectSectionTitle,
    CollectSectionSubtitle,
} from "../styles";

// ─────────────────────────────────────────────────────────────────────────
// RecoveryFunnelPanel — horizontal bar funnel of invoices per recovery state.
// Reads live funnel data from GET /collect/analytics/funnel. Each bar is
// colour-coded by stage tone so the eye can read "healthy → at risk →
// terminal" top-to-bottom.
// ─────────────────────────────────────────────────────────────────────────

interface Props {
    funnel: IRecoveryFunnel | null;
    isLoading: boolean;
}

// Stage label + bar colour, keyed by RecoveryState enum value. Order here is
// only a fallback — the API already returns stages in funnel order.
const STAGE_META: Record<string, { label: string; color: string }> = {
    monitoring: { label: "Monitoring", color: "#9ca3af" },
    due_soon: { label: "Due soon", color: "#60a5fa" },
    due_today: { label: "Due today", color: "#3b82f6" },
    overdue: { label: "Overdue", color: "#f59e0b" },
    active_recovery: { label: "Active recovery", color: "#f97316" },
    promise_to_pay: { label: "Promise to pay", color: colors.blueRibbon },
    partially_paid: { label: "Partially paid", color: "#14b8a6" },
    disputed: { label: "Disputed", color: "#ef4444" },
    manual_review: { label: "Manual review", color: "#dc2626" },
    legal_action: { label: "Legal action", color: "#991b1b" },
    recovered: { label: "Recovered", color: colors.mainSecondary },
    closed: { label: "Closed", color: "#6b7280" },
};

function fmtCompact(n: number, currency = "USD"): string {
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency,
            notation: "compact",
            maximumFractionDigits: 1,
        }).format(n || 0);
    } catch {
        return `$${(n || 0).toFixed(0)}`;
    }
}

const RecoveryFunnelPanel: React.FC<Props> = ({ funnel, isLoading }) => {
    const currency = funnel?.currency ?? "USD";

    // Drop stages that are entirely empty so the chart stays readable; if
    // everything is empty we still show a friendly zero-state below.
    const stages = (funnel?.stages ?? []).filter((s) => s.count > 0);

    const categories = stages.map(
        (s) => STAGE_META[s.state]?.label ?? s.state,
    );
    const counts = stages.map((s) => s.count);
    const barColors = stages.map(
        (s) => STAGE_META[s.state]?.color ?? colors.primary,
    );
    const balances = stages.map((s) => s.balance);

    const options: ApexOptions = {
        chart: {
            type: "bar",
            toolbar: { show: false },
            fontFamily: "'Montserrat', sans-serif",
        },
        plotOptions: {
            bar: {
                horizontal: true,
                distributed: true,
                borderRadius: 4,
                barHeight: "70%",
            },
        },
        colors: barColors,
        dataLabels: {
            enabled: true,
            style: { fontSize: "12px", fontWeight: 700, colors: ["#fff"] },
        },
        legend: { show: false },
        grid: { borderColor: "#eef0f4" },
        xaxis: {
            categories,
            labels: {
                style: { colors: "#6b7280", fontSize: "12px" },
            },
        },
        yaxis: {
            labels: {
                style: { colors: "#374151", fontSize: "12px" },
            },
        },
        tooltip: {
            y: {
                formatter: (val: number, opts: any) => {
                    const idx = opts?.dataPointIndex ?? 0;
                    const bal = balances[idx] ?? 0;
                    return `${val} invoice${val === 1 ? "" : "s"} · ${fmtCompact(
                        bal,
                        currency,
                    )}`;
                },
            },
        },
    };

    const series = [{ name: "Invoices", data: counts }];

    return (
        <CollectSectionCard sx={{ p: 3, height: "100%" }}>
            <CollectSectionTitle>Recovery funnel</CollectSectionTitle>
            <CollectSectionSubtitle>
                Open invoices by recovery stage, healthy to at-risk.
            </CollectSectionSubtitle>

            {isLoading ? (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 320,
                    }}
                >
                    <CircularProgress size={28} sx={{ color: colors.mainSecondary }} />
                </Box>
            ) : stages.length === 0 ? (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 320,
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            color: "#9ca3af",
                            fontSize: "0.9rem",
                        }}
                    >
                        No invoices in the recovery pipeline yet.
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ mt: 1 }}>
                    <ReactApexChart
                        options={options}
                        series={series}
                        type="bar"
                        height={Math.max(280, stages.length * 42)}
                    />
                </Box>
            )}
        </CollectSectionCard>
    );
};

export default RecoveryFunnelPanel;
